import { productApi } from "#/api/productApi";
import { uploadApi } from "#/api/uploadApi";
import ActionButton from "#/components/buttons/ActionButton";
import ImagePicker from "#/components/ui/ImagePicker";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import { ProductShape, type CreateProductPayload, type Product } from "#/types/products";
import { ItemStatus } from "#/types/items";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { getRandomColor, getItemStatusColor } from "#/utils/ColorUtils";
import { Accordion, Button, ColorInput, Divider, Select, Skeleton, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconExternalLink, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";
import { useUnsavedChangesWarning } from "#/hooks/useUnsavedChangesWarning";
import { useClient } from "#/hook/useClient";
import { logger } from "#/utils/logger";
import {
  dimensionToCanonical,
  dimensionToDisplay,
  dimensionUnitLabel,
  getUnitSystem,
  weightToCanonical,
  weightToDisplay,
  weightUnitLabel,
} from "#/utils/unitUtils";

/**
 * A number field's value as the backend should store it. The inputs report a
 * cleared field as "" (and decimals come back from the API as strings), so
 * anything that is not a finite number is an explicit clear: `null`, never 0.
 */
const toNumberOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** A stored number as the form holds it: empty when there is none. */
const toFormNumber = (value: number | string | null | undefined): number | undefined =>
  toNumberOrNull(value) ?? undefined;

/** The editable part of a category, as the form holds it. */
const toFormValues = (product: Product): CreateProductPayload => ({
  name: product.name ?? "",
  description: product.description ?? "",
  sku: product.sku ?? "",
  variant: product.variant ?? "",
  cost: toFormNumber(product.cost),
  retail_price: toFormNumber(product.retail_price),
  procurement_time: toFormNumber(product.procurement_time),
  dwell_time: toFormNumber(product.dwell_time),
  tare_weight: toFormNumber(product.tare_weight),
  filling_capacity: toFormNumber(product.filling_capacity),
  weight: toFormNumber(product.weight),
  length: toFormNumber(product.length),
  height: toFormNumber(product.height),
  width: toFormNumber(product.width),
  shape: product.shape ?? ProductShape.RECTANGULAR,
  diameter: toFormNumber(product.diameter),
  image_url: product.image_url ?? null,
  color: product.color ?? null,
  customize_columns: { ...(product.customize_columns ?? {}) },
  scan_statuses: product.scan_statuses ?? [],
});

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { canWrite, canDelete } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const { client } = useClient();
  const unitSystem = getUnitSystem(client);
  const [searchParams] = useSearchParams();
  const duplicateFrom = searchParams.get("duplicateFrom");
  const [imageFile, setImageFile] = useState<File | null>(null);


  // Helper function to check if a value is a valid ItemStatus enum
  const isValidItemStatus = (value: string | undefined): boolean => {
    if (!value) return false;
    return Object.values(ItemStatus).includes(value as ItemStatus);
  };

  const form = useForm<CreateProductPayload>({
    initialValues: {
      name: "",
      description: "",
      sku: "",
      variant: "",
      cost: undefined,
      retail_price: undefined,
      procurement_time: undefined,
      dwell_time: undefined,
      tare_weight: undefined,
      filling_capacity: undefined,
      weight: undefined,
      length: undefined,
      height: undefined,
      width: undefined,
      shape: ProductShape.RECTANGULAR,
      diameter: undefined,
      image_url: undefined,
      color: undefined,
      scan_statuses: [{ step: 1, label: "" }],
      customize_columns: {},
    },
    validate: (values) => {
      const errors: Record<string, string> = {};

      if (!values.name) errors.name = t("products.form.namePlaceholder");
      if (id === "new" && !values.sku) errors.sku = t("products.form.sku");
      // if (!values.description) errors.description = "Description is required";
      // if (!values.variant) errors.variant = "Variant is required";
      // if (values.cost == null) errors.cost = "Cost is required";
      // if (values.retail_price == null)
      //   errors.retail_price = "Retail price is required";
      // if (values.tare_weight == null)
      //   errors.tare_weight = "Tare weight is required";
      // if (values.filling_capacity == null)
      //   errors.filling_capacity = "Filling capacity is required";

      // if (!values.scan_statuses || values.scan_statuses.length === 0) {
      //   errors["scan_statuses"] = "At least one scan step is required";
      // } else {
      //   values.scan_statuses.forEach((item, index) => {
      //     if (!item.label || item.label.trim() === "") {
      //       errors[`scan_statuses.${index}.label`] = "Step cannot be empty";
      //     }
      //   });
      // }

      return errors;
    },
    transformValues: (values) => ({
      ...values,
      // `null` (not `undefined`) for every cleared optional field below — an
      // explicit clear must actually persist. `undefined` keys get dropped by
      // JSON before the request ever reaches the backend.
      cost: toNumberOrNull(values.cost),
      retail_price: toNumberOrNull(values.retail_price),
      procurement_time: toNumberOrNull(values.procurement_time),
      dwell_time: toNumberOrNull(values.dwell_time),
      tare_weight: toNumberOrNull(values.tare_weight),
      filling_capacity: toNumberOrNull(values.filling_capacity),
      weight: toNumberOrNull(values.weight),
      length: toNumberOrNull(values.length),
      height: toNumberOrNull(values.height),
      width: toNumberOrNull(values.width),
      shape: values.shape,
      diameter: toNumberOrNull(values.diameter),
      scan_statuses: values.scan_statuses?.map((item, index) => {
        return {
          step: index + 1,
          label: item.label,
        };
      }),
    }),
  });

  const handleSubmit = (values: typeof form.values) => {
    // Only skip genuinely untouched (`undefined`) fields — `null` and `""`
    // are legitimate "clear this field" values and must reach the backend,
    // otherwise nothing on this form can ever be cleared once set.
    const payload: Record<string, unknown> = {};

    // ColorInput reports a cleared picker as "" — the backend only accepts a hex color or null.
    if (values.color === "") {
      values = { ...values, color: null };
    }

    (Object.entries(values) as [keyof CreateProductPayload, CreateProductPayload[keyof CreateProductPayload]][]).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      // Skip empty objects (`null` is a deliberate clear, not an empty object)
      if (value !== null && typeof value === "object" && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0) {
        return;
      }

      // For scan_statuses, filter out empty labels
      if (key === "scan_statuses" && Array.isArray(value)) {
        const filtered = (value as { step: number; label: string }[]).filter(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "label" in item &&
            typeof item.label === "string" &&
            item.label.trim() !== ""
        );
        if (filtered.length > 0) {
          payload[key] = filtered;
        }
        return;
      }

      payload[key] = value;
    });

    if (id && product && product.sku) {
      delete payload.sku;
    }

    productMutation.mutate(payload);
  };

  const productMutation = useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      if (imageFile) {
        const url = await uploadApi.upload(imageFile);
        payload = { ...payload, image_url: url };
        form.setFieldValue("image_url", url);
      }
      if (id !== "new") {
        return await productApi.update(Number(id), payload);
      } else {
        return await productApi.create(payload);
      }
    },
    onSuccess: (data) => {
      setImageFile(null);
      if (id === "new") {
        void navigate(`/inventory/categories/${data.data.id}`);
      } else {
        // The form now matches what is stored: make that its clean baseline,
        // keep the cached category in step and re-read only the detail. Item
        // counts (analytics) cannot change from a category edit.
        const saved = toFormValues(data.data);
        form.setInitialValues(saved);
        form.resetDirty(saved);
        queryClient.setQueryData<Product>(["productDetails", id], (current) =>
          current ? { ...current, ...data.data } : data.data,
        );
        void queryClient.invalidateQueries({ queryKey: ["productDetails", id] });
      }
      showSuccessNotification({
        message: id === "new" ? t("products.createCategory") + " - " + t("common.success") : t("products.categoryDetails") + " - " + t("common.success"),
      });
    },
    onError: (error) => {
      logger.error("ProductDetail save error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const { data: product } = useQuery({
    queryKey: ["productDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid categoryId");

      const response = await productApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch category");
      return response.data;
    },
  });

  // Load the fetched category into the form, but never over unsaved edits:
  // React Query refetches on window focus, and that used to wipe what the
  // user was typing. Each fetched object is applied once; while the form is
  // dirty it is left alone and picked up after the next save makes it clean.
  const appliedProductRef = useRef<Product | null>(null);
  useEffect(() => {
    if (!product || appliedProductRef.current === product || form.isDirty()) return;
    const values = toFormValues(product);
    form.setInitialValues(values);
    form.setValues(values);
    form.resetDirty(values);
    appliedProductRef.current = product;
  }, [product, form]);

  // Unsaved edits (or a picked image not yet uploaded) warn before a refresh or tab close.
  useUnsavedChangesWarning(form.isDirty() || imageFile !== null);

  const { data: analytics, isPending: analyticsPending } = useQuery({
    queryKey: ["productAnalytics", id],
    enabled: !!id && id !== "new",
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid categoryId");
      const response = await productApi.getAnalytics(Number(id));
      if (!response.data) throw new Error("Failed to fetch category analytics");
      return response.data;
    },
  });

  // Fetch product to duplicate
  useQuery({
    queryKey: ["duplicateProduct", duplicateFrom],
    enabled: !!duplicateFrom && id === "new",
    queryFn: async () => {
      if (!duplicateFrom) throw new Error("Invalid category to duplicate");

      const response = await productApi.getById(Number(duplicateFrom));
      if (!response.data) throw new Error("Failed to fetch category to duplicate");
      const product = response.data;

      // Pre-fill the new category from the copied one (no id, SKU must be unique).
      // Only runs for id === "new", where the detail query is disabled, so it
      // never competes with the apply-once effect above.
      form.setValues({
        ...toFormValues(product),
        name: product.name ? `${product.name} (Copy)` : "",
        sku: "",
      });
      form.resetDirty();
      return product;
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("products.categoryDetails") : t("products.createCategory")}
      navRightSection={
        <div className={`w-full flex  justify-end gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={productMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="w-full grid grid-cols-[70%_1fr] gap-4">
            <div className="w-full grid grid-cols-1 gap-4">
              <Accordion
                variant={"separated"}
                defaultValue={["basic", "pricing", "physical_specs", "operations", "steps", "customize_columns"]}
                multiple
              >
                <Accordion.Item value={"basic"}>
              <Accordion.Control>{t("common.basicDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4 ">
                  <div className="w-full grid grid-cols-[auto_auto_1fr] gap-6 items-stretch">
                    <ImagePicker
                      variant="box"
                      label={t("products.form.image")}
                      value={form.values.image_url}
                      disabled={!canWrite()}
                      clearable
                      onChange={(file) => {
                        setImageFile(file);
                        if (!file) form.setFieldValue("image_url", null);
                      }}
                    />

                    <Divider orientation="vertical" />

                    <div className="flex flex-col gap-4">
                      <TextInput
                        label={t("products.form.name")}
                        withAsterisk
                        disabled={!canWrite()}
                        placeholder={t("products.form.namePlaceholder")}
                        {...form.getInputProps("name")}
                      />

                      {(id === "new" || !product?.sku) && (
                        <TextInput
                          label={t("products.form.sku")}
                          withAsterisk={id === "new"}
                          disabled={!canWrite()}
                          placeholder={t("products.form.sku")}
                          {...form.getInputProps("sku")}
                        />
                      )}

                      <TextInput
                        label={t("products.form.type")}
                        disabled={!canWrite()}
                        placeholder={t("products.form.type")}
                        {...form.getInputProps("variant")}
                      />

                      <ColorInput
                        label={t("products.form.color")}
                        placeholder={t("products.form.colorPlaceholder")}
                        disabled={!canWrite()}
                        value={form.values.color ?? ""}
                        onChange={(value) => form.setFieldValue("color", value || null)}
                      />
                    </div>
                  </div>

                  <Textarea
                    label={t("products.form.description")}
                    minRows={20}
                    disabled={!canWrite()}
                    classNames={{
                      input: "h-38",
                    }}
                    placeholder={t("products.form.descriptionPlaceholder")}
                    {...form.getInputProps("description")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"pricing"}>
              <Accordion.Control>{t("products.sections.pricing")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-2 gap-4">
                  <TextInput
                    label={t("products.columns.cost")}
                    type="number"
                    disabled={!canWrite()}
                    placeholder={t("products.columns.cost")}
                    {...form.getInputProps("cost")}
                  />
                  <TextInput
                    label={t("products.columns.retailPrice")}
                    type="number"
                    disabled={!canWrite()}
                    placeholder={t("products.columns.retailPrice")}
                    {...form.getInputProps("retail_price")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"physical_specs"}>
              <Accordion.Control>{t("products.sections.physicalSpecs")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <div className="w-full grid grid-cols-3 gap-4">
                    <TextInput
                      label={t("products.columns.fillingCapacity")}
                      description={t("products.form.fillingCapacityTooltip")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.fillingCapacity")}
                      {...form.getInputProps("filling_capacity")}
                    />

                    <TextInput
                      label={t("products.columns.tareWeight")}
                      description={t("products.form.tareWeightTooltip")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.tareWeight")}
                      {...form.getInputProps("tare_weight")}
                    />

                    <TextInput
                      label={`${t("products.form.weight")} (${weightUnitLabel(unitSystem)})`}
                      description={t("products.form.weightTooltip")}
                      type="number"
                      disabled={!canWrite()}
                      value={
                        form.values.weight != null
                          ? (weightToDisplay(form.values.weight, unitSystem) ?? "")
                          : ""
                      }
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        form.setFieldValue(
                          "weight",
                          raw === "" ? undefined : weightToCanonical(Number(raw), unitSystem)
                        );
                      }}
                    />
                  </div>

                  <Select
                    label={t("products.form.shape")}
                    description={t("products.form.shapeTooltip")}
                    disabled={!canWrite()}
                    data={[
                      { value: ProductShape.RECTANGULAR, label: t("products.form.rectangular") },
                      { value: ProductShape.CYLINDRICAL, label: t("products.form.cylindrical") },
                    ]}
                    value={form.values.shape ?? ProductShape.RECTANGULAR}
                    onChange={(value) =>
                      form.setFieldValue(
                        "shape",
                        (value as ProductShape | null) ?? ProductShape.RECTANGULAR
                      )
                    }
                  />

                  {form.values.shape === ProductShape.CYLINDRICAL ? (
                    <div className="w-full grid grid-cols-2 gap-4">
                      <TextInput
                        label={`${t("products.form.diameter")} (${dimensionUnitLabel(unitSystem)})`}
                        description={t("products.form.diameterTooltip")}
                        type="number"
                        disabled={!canWrite()}
                        value={
                          form.values.diameter != null
                            ? (dimensionToDisplay(form.values.diameter, unitSystem) ?? "")
                            : ""
                        }
                        onChange={(event) => {
                          const raw = event.currentTarget.value;
                          form.setFieldValue(
                            "diameter",
                            raw === "" ? undefined : dimensionToCanonical(Number(raw), unitSystem)
                          );
                        }}
                      />

                      <TextInput
                        label={`${t("products.form.height")} (${dimensionUnitLabel(unitSystem)})`}
                        description={t("products.form.heightTooltip")}
                        type="number"
                        disabled={!canWrite()}
                        value={
                          form.values.height != null
                            ? (dimensionToDisplay(form.values.height, unitSystem) ?? "")
                            : ""
                        }
                        onChange={(event) => {
                          const raw = event.currentTarget.value;
                          form.setFieldValue(
                            "height",
                            raw === "" ? undefined : dimensionToCanonical(Number(raw), unitSystem)
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full grid grid-cols-3 gap-4">
                      <TextInput
                        label={`${t("products.form.length")} (${dimensionUnitLabel(unitSystem)})`}
                        description={t("products.form.lengthTooltip")}
                        type="number"
                        disabled={!canWrite()}
                        value={
                          form.values.length != null
                            ? (dimensionToDisplay(form.values.length, unitSystem) ?? "")
                            : ""
                        }
                        onChange={(event) => {
                          const raw = event.currentTarget.value;
                          form.setFieldValue(
                            "length",
                            raw === "" ? undefined : dimensionToCanonical(Number(raw), unitSystem)
                          );
                        }}
                      />

                      <TextInput
                        label={`${t("products.form.height")} (${dimensionUnitLabel(unitSystem)})`}
                        description={t("products.form.heightTooltip")}
                        type="number"
                        disabled={!canWrite()}
                        value={
                          form.values.height != null
                            ? (dimensionToDisplay(form.values.height, unitSystem) ?? "")
                            : ""
                        }
                        onChange={(event) => {
                          const raw = event.currentTarget.value;
                          form.setFieldValue(
                            "height",
                            raw === "" ? undefined : dimensionToCanonical(Number(raw), unitSystem)
                          );
                        }}
                      />

                      <TextInput
                        label={`${t("products.form.width")} (${dimensionUnitLabel(unitSystem)})`}
                        description={t("products.form.widthTooltip")}
                        type="number"
                        disabled={!canWrite()}
                        value={
                          form.values.width != null
                            ? (dimensionToDisplay(form.values.width, unitSystem) ?? "")
                            : ""
                        }
                        onChange={(event) => {
                          const raw = event.currentTarget.value;
                          form.setFieldValue(
                            "width",
                            raw === "" ? undefined : dimensionToCanonical(Number(raw), unitSystem)
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"operations"}>
              <Accordion.Control>{t("products.sections.operations")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-2 gap-4">
                  <TextInput
                    label={t("products.form.procurementTime")}
                    description={t("products.form.procurementTimeHint")}
                    type="number"
                    min={0}
                    disabled={!canWrite()}
                    placeholder={t("products.form.procurementTimePlaceholder")}
                    {...form.getInputProps("procurement_time")}
                  />

                  <TextInput
                    label={t("products.form.dwellTime")}
                    description={t("products.form.dwellTimeHint")}
                    type="number"
                    min={0}
                    disabled={!canWrite()}
                    placeholder={t("products.form.dwellTimePlaceholder")}
                    {...form.getInputProps("dwell_time")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"steps"}>
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>
                    {t("products.scanSteps")}{" "}
                    {(form.values.scan_statuses?.length ?? 0) > 0
                      ? `( ${form.values.scan_statuses!.length} )`
                      : ""}
                  </span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size={"xs"}
                      icon={IconPlus}
                      disabled={
                        !canWrite() ||
                        (form.values.scan_statuses &&
                        form.values.scan_statuses.length >= 10)
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        form.setValues({
                          ...form.values,
                          scan_statuses: [
                            ...(form?.values?.scan_statuses ?? []),
                            {
                              step: form.values?.scan_statuses
                                ? form.values?.scan_statuses.length + 1
                                : 1,
                              label: "",
                            },
                          ],
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <div className="w-full flex flex-col gap-1">
                    {form.values?.scan_statuses?.map((scanStatus, index) => {
                      const currentLabel = scanStatus.label;
                      const isValidStatus = isValidItemStatus(currentLabel);

                      return (
                        <div
                          key={`step_${index}`}
                          className="w-full grid grid-cols-[1fr_auto] gap-4 items-center"
                        >
                          <Select
                            label={`${t("products.scanSteps")} # ${index + 1}`}
                            placeholder={t("common.selectStatus")}
                            searchable
                            disabled={!canWrite()}
                            description={
                              !isValidStatus && currentLabel
                                ? `Old Value: ${currentLabel}`
                                : undefined
                            }
                            data={allowedStatuses.map((status) => ({
                              value: status,
                              label: sanitizeItemStatusLabel(status),
                            }))}
                            {...form.getInputProps(
                              `scan_statuses.${index}.label`
                            )}
                          />

                          <div className="mt-6">
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                disabled={index === 0 || !canDelete()}
                                color="red.5"
                                onClick={() => {
                                  const newSteps = [
                                    ...(form.values.scan_statuses ?? []),
                                  ];
                                  newSteps.splice(index, 1);
                                  form.setFieldValue("scan_statuses", newSteps);
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      disabled={!canWrite()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            {/* Editable Key input */}
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return; // avoid duplicates

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            {/* Editable Value input */}
                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            {/* Delete Button */}
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                disabled={!canDelete()}
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
              </Accordion>
            </div>

            <div className="w-full grid grid-cols-1 gap-4">
              {id !== "new" && product && (
                <Accordion
                  variant={"separated"}
                  defaultValue={product.sku ? ["sku", "analytics"] : ["analytics"]}
                  multiple
                >
                  {product.sku && (
                    <Accordion.Item value={"sku"} disabled>
                      <Accordion.Control>SKU</Accordion.Control>
                      <Accordion.Panel>
                        <div className="text-base font-medium">{product.sku}</div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  <Accordion.Item value={"analytics"}>
                    <Accordion.Control>{t("common.analytics")}</Accordion.Control>
                    <Accordion.Panel>
                      <div className="w-full flex flex-col gap-4">
                        {analyticsPending && (
                          <>
                            <Skeleton height={88} radius="md" />
                            <Skeleton height={160} radius="md" />
                            <Skeleton height={160} radius="md" />
                          </>
                        )}
                        {!analyticsPending && (
                          <>
                        {/* Total Items Card */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="text-sm font-semibold text-emerald-800">{t("products.analytics.totalItems")}</div>
                          <div className="text-3xl font-extrabold mt-2 text-emerald-600">
                            {analytics?.totalItems ?? 0}
                          </div>
                        </div>

                        {/* Sites Card */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="text-sm font-semibold">{t("products.analytics.sites")}</div>
                          <div className="text-xs text-gray-500 mb-4">{t("products.analytics.distributionBySites")}</div>
                          {analytics && analytics.bySite.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                              {analytics.bySite.map((site) => {
                                const color = getRandomColor(site.name);
                                return (
                                  <div
                                    key={site.id}
                                    className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow min-w-[120px]"
                                    style={{
                                      borderColor: `var(--mantine-color-${color}-3)`,
                                      backgroundColor: `var(--mantine-color-${color}-0)`,
                                    }}
                                    onClick={() => window.open(`/sites/${site.id}`, '_blank')}
                                  >
                                    <div className="flex justify-between items-start mb-3">
                                      <div
                                        className="text-2xl font-extrabold"
                                        style={{ color: `var(--mantine-color-${color}-7)` }}
                                      >
                                        {site.count}
                                      </div>
                                      <IconExternalLink size={14} className="opacity-60" />
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">{site.name}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400 text-center py-4">{t("products.analytics.noData")}</div>
                          )}
                        </div>

                        {/* Status Card */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="text-sm font-semibold">{t("products.analytics.status")}</div>
                          <div className="text-xs text-gray-500 mb-4">{t("products.analytics.breakdownByStatus")}</div>
                          {analytics && Object.keys(analytics.byStatus).length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(analytics.byStatus).map(([status, count]) => {
                                const color = getItemStatusColor(status);
                                return (
                                  <div
                                    key={status}
                                    className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow min-w-[120px]"
                                    style={{
                                      borderColor: `var(--mantine-color-${color}-3)`,
                                      backgroundColor: `var(--mantine-color-${color}-0)`
                                    }}
                                    onClick={() => window.open(`/inventory/items?status=${status}&categoryId=${id}`, '_blank')}
                                  >
                                    <div className="flex justify-between items-start mb-3">
                                      <div
                                        className="text-2xl font-extrabold"
                                        style={{ color: `var(--mantine-color-${color}-7)` }}
                                      >
                                        {count}
                                      </div>
                                      <IconExternalLink size={14} className="opacity-60" />
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">{sanitizeItemStatusLabel(status)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400 text-center py-4">{t("products.analytics.noData")}</div>
                          )}
                        </div>
                          </>
                        )}
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              )}
            </div>
          </div>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default ProductDetailPage;
